/**
 * Attachments — the read side of files on a record.
 *
 *   AttachmentList → a grid (or compact list) of thumbnail/icon cards; image
 *                    attachments open in the Lightbox. The page owns the data
 *                    and the optional download/remove actions.
 *   Lightbox       → a full-screen image viewer (Portal + blanket) with
 *                    prev/next, a caption, and keyboard (←/→/Esc). Controlled
 *                    via `index` (null = closed).
 */
import { type Component, For, Show, createEffect, createSignal, onCleanup, type JSX } from "solid-js";
import { Dynamic, Portal } from "solid-js/web";
import FileIcon from "lucide-solid/icons/file";
import FileText from "lucide-solid/icons/file-text";
import ImageIcon from "lucide-solid/icons/image";
import FileSpreadsheet from "lucide-solid/icons/file-spreadsheet";
import FileArchive from "lucide-solid/icons/file-archive";
import Download from "lucide-solid/icons/download";
import Trash2 from "lucide-solid/icons/trash-2";
import X from "lucide-solid/icons/x";
import ChevronLeft from "lucide-solid/icons/chevron-left";
import ChevronRight from "lucide-solid/icons/chevron-right";
import Eye from "lucide-solid/icons/eye";
import { cn } from "./utils/cn";
import { focusRing } from "./utils/focus-ring";

type IconComponent = Component<{ size?: number | string; class?: string }>;

export interface Attachment {
  id: string;
  name: string;
  size?: number;
  type?: string;
  url?: string;
  thumbnail?: string;
}

const isImage = (a: Attachment): boolean =>
  (a.type?.startsWith("image/") ?? false) || /\.(png|jpe?g|gif|webp|svg|avif)$/i.test(a.name);
const ext = (name: string): string => name.split(".").pop()?.toLowerCase() ?? "";
function fileIcon(a: Attachment): IconComponent {
  if (isImage(a)) return ImageIcon;
  const e = ext(a.name);
  if (["pdf", "doc", "docx", "txt", "md", "rtf"].includes(e)) return FileText;
  if (["xls", "xlsx", "csv", "numbers"].includes(e)) return FileSpreadsheet;
  if (["zip", "rar", "7z", "tar", "gz"].includes(e)) return FileArchive;
  return FileIcon;
}
export function formatBytes(n?: number): string {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

const iconBtn = cn(
  "inline-flex size-7 items-center justify-center rounded-[3px] bg-[color:var(--c9-surface-overlay)]",
  "text-[color:var(--c9-icon-subtle)] shadow-[var(--c9-shadow-raised)] hover:bg-[var(--c9-bg-neutral-subtle-hovered)]",
  focusRing,
);

/* Lightbox — a full-screen image viewer (Portal + blanket) with prev/next, a
   caption, and keyboard (←/→/Esc). Controlled via `index` (null = closed). */
export function Lightbox(props: {
  items: { id: string; name: string; url?: string }[];
  index: number | null;
  onClose: () => void;
  onIndexChange: (i: number) => void;
}): JSX.Element {
  const cur = () => (props.index != null ? props.items[props.index] : undefined);
  const go = (d: number) => {
    if (props.index == null) return;
    const next = (props.index + d + props.items.length) % props.items.length;
    props.onIndexChange(next);
  };
  createEffect(() => {
    if (props.index == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onClose();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    onCleanup(() => window.removeEventListener("keydown", onKey));
  });

  return (
    <Show when={props.index != null && cur()}>
      <Portal>
        <div
          class="fixed inset-0 flex items-center justify-center p-600"
          style={{ "z-index": "var(--c9-z-modal)" }}
        >
          <div
            aria-hidden="true"
            onClick={() => props.onClose()}
            class="fixed inset-0 bg-[var(--c9-blanket)]"
          />
          <button
            type="button"
            aria-label="Close"
            onClick={() => props.onClose()}
            class={cn(
              "absolute right-300 top-300 inline-flex size-9 items-center justify-center rounded-[3px]",
              "text-[color:var(--c9-text-inverse)] hover:bg-white/15",
              focusRing,
            )}
          >
            <X size={22} />
          </button>
          <Show when={props.items.length > 1}>
            <button
              type="button"
              aria-label="Previous"
              onClick={() => go(-1)}
              class={cn(
                "absolute left-300 top-1/2 inline-flex size-10 -translate-y-1/2 items-center justify-center rounded-full",
                "text-[color:var(--c9-text-inverse)] hover:bg-white/15",
                focusRing,
              )}
            >
              <ChevronLeft size={26} />
            </button>
            <button
              type="button"
              aria-label="Next"
              onClick={() => go(1)}
              class={cn(
                "absolute right-300 top-1/2 inline-flex size-10 -translate-y-1/2 items-center justify-center rounded-full",
                "text-[color:var(--c9-text-inverse)] hover:bg-white/15",
                focusRing,
              )}
            >
              <ChevronRight size={26} />
            </button>
          </Show>
          <figure class="relative flex max-h-full max-w-full flex-col items-center gap-200">
            <img
              src={cur()!.url}
              alt={cur()!.name}
              class="max-h-[80vh] max-w-[86vw] rounded-medium object-contain shadow-[var(--c9-shadow-overlay)]"
            />
            <figcaption class="text-body text-[color:var(--c9-text-inverse)]">
              {cur()!.name}
              <Show when={props.items.length > 1}>
                <span class="text-[color:var(--c9-text-inverse)]/70">
                  {" · "}
                  {props.index! + 1} of {props.items.length}
                </span>
              </Show>
            </figcaption>
          </figure>
        </div>
      </Portal>
    </Show>
  );
}

/* AttachmentList — view existing files on a record (the read side). Grid of
   thumbnail/icon cards or a compact list; image attachments open in the Lightbox.
   Optional download/remove actions. The page owns the data. */
export function AttachmentList(props: {
  items: Attachment[];
  layout?: "grid" | "list";
  onDownload?: (a: Attachment) => void;
  onRemove?: (a: Attachment) => void;
  class?: string;
}): JSX.Element {
  const [lightbox, setLightbox] = createSignal<number | null>(null);
  const images = () => props.items.filter(isImage);
  const openImage = (a: Attachment) => {
    const i = images().findIndex((x) => x.id === a.id);
    if (i >= 0) setLightbox(i);
  };

  const list = () => props.layout === "list";

  return (
    <>
      <Show
        when={list()}
        fallback={
          <div class={cn("grid grid-cols-2 gap-150 sm:grid-cols-3 lg:grid-cols-4", props.class)}>
            <For each={props.items}>
              {(a) => (
                <div class="group relative flex flex-col overflow-hidden rounded-medium border border-[color:var(--c9-border)] bg-[color:var(--c9-surface)]">
                  <button
                    type="button"
                    onClick={() => isImage(a) && a.url && openImage(a)}
                    class={cn(
                      "flex aspect-[4/3] items-center justify-center overflow-hidden bg-[color:var(--c9-surface-sunken)]",
                      isImage(a) && a.url ? "cursor-zoom-in" : "cursor-default",
                    )}
                  >
                    <Show
                      when={isImage(a) && (a.thumbnail || a.url)}
                      fallback={
                        <Dynamic
                          component={fileIcon(a)}
                          size={28}
                          class="text-[color:var(--c9-icon-subtle)]"
                        />
                      }
                    >
                      <img src={a.thumbnail || a.url} alt={a.name} class="size-full object-cover" />
                    </Show>
                  </button>
                  <div class="flex min-w-0 items-center gap-100 px-150 py-100">
                    <span class="min-w-0 flex-1">
                      <span class="block truncate text-body-s font-[570] text-[color:var(--c9-text)]">
                        {a.name}
                      </span>
                      <Show when={a.size != null}>
                        <span class="block text-body-s text-[color:var(--c9-text-subtlest)]">
                          {formatBytes(a.size)}
                        </span>
                      </Show>
                    </span>
                  </div>
                  {/* hover actions */}
                  <div class="absolute right-100 top-100 flex gap-050 opacity-0 transition-opacity group-hover:opacity-100">
                    <Show when={isImage(a) && a.url}>
                      <button type="button" aria-label="Preview" class={iconBtn} onClick={() => openImage(a)}>
                        <Eye size={14} />
                      </button>
                    </Show>
                    <Show when={props.onDownload}>
                      <button type="button" aria-label="Download" class={iconBtn} onClick={() => props.onDownload!(a)}>
                        <Download size={14} />
                      </button>
                    </Show>
                    <Show when={props.onRemove}>
                      <button type="button" aria-label="Remove" class={iconBtn} onClick={() => props.onRemove!(a)}>
                        <Trash2 size={14} />
                      </button>
                    </Show>
                  </div>
                </div>
              )}
            </For>
          </div>
        }
      >
        <ul class={cn("flex flex-col overflow-hidden rounded-medium border border-[color:var(--c9-border)]", props.class)}>
          <For each={props.items}>
            {(a, i) => (
              <li
                class={cn(
                  "group flex items-center gap-150 px-200 py-150 hover:bg-[var(--c9-bg-neutral-subtle-hovered)]",
                  i() > 0 && "border-t border-[color:var(--c9-border)]",
                )}
              >
                <span class="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-small bg-[color:var(--c9-surface-sunken)]">
                  <Show
                    when={isImage(a) && (a.thumbnail || a.url)}
                    fallback={
                      <Dynamic
                        component={fileIcon(a)}
                        size={18}
                        class="text-[color:var(--c9-icon-subtle)]"
                      />
                    }
                  >
                    <img src={a.thumbnail || a.url} alt={a.name} class="size-full object-cover" />
                  </Show>
                </span>
                <span class="min-w-0 flex-1">
                  <span class="block truncate text-body font-[570] text-[color:var(--c9-text)]">
                    {a.name}
                  </span>
                  <Show when={a.size != null}>
                    <span class="block text-body-s text-[color:var(--c9-text-subtlest)]">
                      {formatBytes(a.size)}
                    </span>
                  </Show>
                </span>
                <span class="flex shrink-0 items-center gap-050 opacity-0 transition-opacity group-hover:opacity-100">
                  <Show when={isImage(a) && a.url}>
                    <button type="button" aria-label="Preview" class={iconBtn} onClick={() => openImage(a)}>
                      <Eye size={14} />
                    </button>
                  </Show>
                  <Show when={props.onDownload}>
                    <button type="button" aria-label="Download" class={iconBtn} onClick={() => props.onDownload!(a)}>
                      <Download size={14} />
                    </button>
                  </Show>
                  <Show when={props.onRemove}>
                    <button type="button" aria-label="Remove" class={iconBtn} onClick={() => props.onRemove!(a)}>
                      <Trash2 size={14} />
                    </button>
                  </Show>
                </span>
              </li>
            )}
          </For>
        </ul>
      </Show>

      <Lightbox
        items={images().map((a) => ({ id: a.id, name: a.name, url: a.url }))}
        index={lightbox()}
        onClose={() => setLightbox(null)}
        onIndexChange={setLightbox}
      />
    </>
  );
}
